/**
 * OptimizeSheet - one OPTIMIZE button, and everything the solver needs behind it.
 *
 * 🔴 WHAT THIS REPLACED, AND WHY IT IS FEWER DECISIONS RATHER THAN FEWER
 * PIXELS. The toolbar carried five controls: an OPTIMIZE FOR toggle, a BUDGET
 * toggle, "From targets", "Fill empty slots" and "Rebuild". Four of them do
 * nothing on their own, two of them are settings wearing the same clothes as
 * the actions beside them, and the user had to work out for himself that the
 * two toggles CROSS the two buttons. Nothing on that row said "press this to
 * optimize", which is the only thing most people came to the row to do.
 *
 * So: one filled orange button that says OPTIMIZE, the same treatment the Live
 * Board's DRAFT OPTIONS & SHARING uses, because this is a place you go rather
 * than a state you are in. Behind it the three questions in the order you would
 * ask them out loud - what am I maximizing, what may it spend, and what may it
 * move - each pre-selected from what the plan already says, and one GO.
 *
 * 🔴 THE CHIPS WRITE THE PLAN IMMEDIATELY, exactly as the two toggles did.
 * Objective and budget mode are SETTINGS: they persist, they are part of the
 * plan's fingerprint, and a plan whose objective changed is legitimately stale
 * before anything is re-solved. Buffering them until GO would break that
 * handshake and quietly make the stale marker lie. Scope is the one thing that
 * is not a setting - it is the verb - so it is local state that dies with the
 * sheet.
 *
 * 🔴 AN INLINE SHEET, NEVER window.confirm. A native dialog cannot show chips,
 * cannot pre-select from the plan, and blocks the page it is asking about.
 */

import React, { useEffect, useRef, useState } from "react";
import { theme } from "../../data/theme";
import { useIsNarrow, PHONE_BREAKPOINT } from "./useViewport";

export const OPTIMIZE_LABEL = "OPTIMIZE";

/** Maximize: what the solver is scoring. Writes plan.objective. */
export const OBJECTIVE_CHIPS = [
  { key: "ppg", label: "PPG", title: "Maximise projected points. The straight “who scores most” build." },
  { key: "vorp", label: "VORP", title: "Maximise value over replacement. Prefers positions where the drop-off after your starter is steepest." },
];

/**
 * Budget: what the money constraint IS. Writes plan.budgetMode.
 *
 * 🔴 THE LABELS SAY WHAT HAPPENS, NOT WHAT THE MODE IS CALLED. "TOTAL" and
 * "PER SPOT" were internal names on screen: correct, short, and no help at all
 * to somebody deciding between them. The total title interpolates the plan's
 * OWN budget rather than a hardcoded $200, because a $240 league reading "$200
 * pot" learns the wrong thing about its own plan.
 */
export const budgetChips = (budget) => [
  {
    key: "slot",
    label: "Use my per spot budgets",
    title: "Every spot you gave a budget only takes a player at or under it.",
  },
  {
    key: "total",
    label: "Ignore them, just maximize on my total budget",
    title: `One $${Math.round(Number(budget) || 0)} pot. Spend it wherever it buys the most PPG; the per spot figures are display only.`,
  },
];

/** Scope: what the solver may MOVE. Local to the sheet; not a plan setting. */
export const SCOPE_CHIPS = [
  {
    key: "fill",
    label: "Fill empty spots",
    title: "Fills only the blanks. Every player already on the plan is protected.",
  },
  {
    key: "rebuild",
    label: "Rebuild everything",
    title: "Drops every unlocked player and re-decides. Your locks are kept at what you paid.",
  },
];

/**
 * The stale sentence, which used to live in the Rebuild button's tooltip where
 * it was only readable by somebody already hovering the button they had not
 * worked out they needed. It is shown, not hovered, under the scope it argues
 * for.
 */
export const STALE_EXPLANATION =
  "Something the last solve depended on has changed: a price, a budget, a lock or the objective. Rebuilding re-solves from scratch, keeping only your locks.";

function Chip({ on, label, title, onClick, testId }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={on}
      data-testid={testId}
      title={title}
      onClick={onClick}
      style={{
        padding: `${theme.spacing.sm}px ${theme.spacing.lg}px`,
        borderRadius: theme.radius.md,
        border: `1px solid ${on ? theme.colors.border.brand : theme.colors.border.default}`,
        background: on ? theme.helpers.brandBg(0.16) : theme.colors.bg.raised,
        color: on ? theme.colors.brand.orange : theme.colors.text.secondary,
        fontSize: theme.fontSize.sub,
        fontWeight: theme.fontWeight.bold,
        fontFamily: theme.fonts.body,
        cursor: "pointer",
        textAlign: "left",
      }}
    >{label}</button>
  );
}

function Row({ label, children, aside }) {
  return (
    <div style={{ marginBottom: theme.spacing.lg }}>
      <div style={{
        display: "flex", alignItems: "center", gap: theme.spacing.sm,
        marginBottom: theme.spacing.sm,
      }}>
        <span style={{
          fontSize: theme.fontSize.caption,
          fontWeight: theme.fontWeight.heavy,
          letterSpacing: 0.7,
          textTransform: "uppercase",
          color: theme.colors.text.faint,
        }}>{label}</span>
        {aside}
      </div>
      <div style={{ display: "flex", gap: theme.spacing.sm, flexWrap: "wrap" }}>
        {children}
      </div>
    </div>
  );
}

export default function OptimizeSheet({
  objective,
  onObjectiveChange,
  budgetMode,
  onBudgetModeChange,
  budget = 0,
  onFillFromTargets,
  isStale = false,
  onFill,
  onRebuild,
}) {
  const [open, setOpen] = useState(false);
  // 🔴 SEEDED WHEN THE SHEET OPENS, not held across openings. A plan that has
  // gone stale since you last looked must open on Rebuild, and a plan you just
  // rebuilt must not keep offering it.
  const [scope, setScope] = useState("fill");
  const wrapRef = useRef(null);
  const buttonRef = useRef(null);
  const phone = useIsNarrow(PHONE_BREAKPOINT);

  /**
   * 🔴 THE PHONE SHEET IS FIXED AND MEASURED, and the reason is that the
   * desktop one cannot simply be narrowed. It is 372px wide at `left: 0` of a
   * button that sits at the RIGHT end of the toolbar, so on a 412px screen it
   * runs about 240px off the edge — shrinking it to fit would leave a sheet
   * pinned under the last control with the budget chips clipped anyway.
   *
   * Fixed positioning is what buys the full viewport width; the cost is that a
   * fixed element does not travel with the page, so the top is re-measured off
   * the button on scroll and on resize while the sheet is open. Capture-phase
   * scroll, because the page's scroller may be an ancestor rather than window.
   */
  const [sheetTop, setSheetTop] = useState(0);
  useEffect(() => {
    if (!open || !phone) return undefined;
    const place = () => {
      const el = buttonRef.current;
      if (!el || !el.getBoundingClientRect) return;
      setSheetTop(el.getBoundingClientRect().bottom + 6);
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open, phone]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const toggle = () => {
    setOpen((v) => {
      if (!v) setScope(isStale ? "rebuild" : "fill");
      return !v;
    });
  };

  const go = () => {
    setOpen(false);
    if (scope === "rebuild") { if (onRebuild) onRebuild(); return; }
    if (onFill) onFill();
  };

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      {/* 🔴 THE STALE MARKER MOVED ONTO THE BUTTON. It used to be the Rebuild
          button going orange and renaming itself, which only worked while
          Rebuild was on screen. The prompt has to survive the button it used to
          live on, so it rides the one control that is always there: a dot and
          one word, which is a prompt rather than an action. Nothing here
          re-solves on its own. */}
      <button
        ref={buttonRef}
        type="button"
        data-testid="optimize-button"
        onClick={toggle}
        aria-haspopup="dialog"
        aria-expanded={open}
        style={{
          display: "flex", alignItems: "center", gap: 6,
          background: theme.colors.brand.orange,
          border: `1px solid ${theme.colors.brand.orange}`,
          borderRadius: theme.radius.md,
          color: "#fff",
          fontFamily: "inherit",
          fontSize: theme.fontSize.caption,
          fontWeight: theme.fontWeight.bold,
          letterSpacing: "0.04em",
          padding: "5px 14px",
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
      >
        {OPTIMIZE_LABEL}
        {isStale && (
          <span data-testid="optimize-stale" style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{
              width: 6, height: 6, borderRadius: "50%",
              background: "#fff", display: "inline-block",
            }} />
            <span style={{ fontWeight: theme.fontWeight.bold, textTransform: "none", letterSpacing: 0 }}>changed</span>
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="optimize-sheet"
          role="dialog"
          aria-label="Optimize this plan"
          style={{
            zIndex: 130,
            maxHeight: "70vh", overflowY: "auto",
            ...(phone
              ? { position: "fixed", top: sheetTop, left: 0, right: 0, width: "auto" }
              : { position: "absolute", top: "calc(100% + 6px)", left: 0, width: 372 }),
            background: theme.colors.extended.modalBg,
            border: `1px solid ${theme.colors.border.default}`,
            borderRadius: theme.radius.lg,
            padding: theme.spacing.lg,
            boxShadow: "0 18px 44px rgba(0,0,0,0.55)",
          }}
        >
          <Row label="Maximize">
            {OBJECTIVE_CHIPS.map((o) => (
              <Chip
                key={o.key}
                testId={`objective-${o.key}`}
                on={objective === o.key}
                label={o.label}
                title={o.title}
                onClick={() => onObjectiveChange && onObjectiveChange(o.key)}
              />
            ))}
          </Row>

          {/* 🔴 THE LINK ONLY EXISTS IN THE MODE IT MEANS SOMETHING IN. Writing
              sixteen rail figures while the solver is told to ignore them is a
              button that changes nothing anybody can see. It sits beside the
              row rather than in it because it is an ACTION among settings. */}
          <Row
            label="Budget"
            aside={budgetMode === "slot" && (
              <button
                type="button"
                data-testid="from-targets"
                onClick={() => onFillFromTargets && onFillFromTargets()}
                title="Set every spot's budget to its current target price, and every empty spot to the board's target for it."
                style={{
                  border: "none",
                  background: "transparent",
                  padding: 0,
                  color: theme.colors.brand.accent,
                  fontFamily: theme.fonts.body,
                  fontSize: theme.fontSize.caption,
                  fontWeight: theme.fontWeight.bold,
                  cursor: "pointer",
                  textDecoration: "underline",
                  textDecorationStyle: "dotted",
                }}
              >From targets</button>
            )}
          >
            {budgetChips(budget).map((o) => (
              <Chip
                key={o.key}
                testId={`budget-mode-${o.key}`}
                on={budgetMode === o.key}
                label={o.label}
                title={o.title}
                onClick={() => onBudgetModeChange && onBudgetModeChange(o.key)}
              />
            ))}
          </Row>

          <Row label="Scope">
            {SCOPE_CHIPS.map((o) => (
              <Chip
                key={o.key}
                testId={`scope-${o.key}`}
                on={scope === o.key}
                label={o.label}
                title={o.title}
                onClick={() => setScope(o.key)}
              />
            ))}
          </Row>

          {isStale && (
            <div data-testid="stale-explanation" style={{
              marginTop: -theme.spacing.sm,
              marginBottom: theme.spacing.lg,
              fontSize: theme.fontSize.caption,
              lineHeight: 1.4,
              color: theme.colors.text.muted,
            }}>{STALE_EXPLANATION}</div>
          )}

          <button
            type="button"
            data-testid="optimize-go"
            onClick={go}
            style={{
              width: "100%",
              background: theme.colors.brand.orange,
              border: `1px solid ${theme.colors.brand.orange}`,
              borderRadius: theme.radius.md,
              color: "#fff",
              fontFamily: "inherit",
              fontSize: theme.fontSize.sub,
              fontWeight: theme.fontWeight.heavy,
              letterSpacing: "0.06em",
              padding: `${theme.spacing.sm}px 0`,
              cursor: "pointer",
            }}
          >GO</button>
        </div>
      )}
    </div>
  );
}
